import { useState, useEffect, useRef, useCallback } from 'react';
import { getPdfUrlApi, IOutput, IInput } from '@umu/umu-api/src/services/pdfjs/pdfjs';
import { loadScriptAsync, $window } from '@umu/umu-dom';
import { checkIsAuthCDNUrl } from '@umu/umu-util';
import { isIos, iosVersion } from '@umu/umu-ua';
import { ResourceStatus } from '@umu/umu-types';
import { configRenderHelper } from '../services/configRenderHelper';
import { IPdfReaderProps } from '../types';
import { isHarmonyOSDevice, isIOSSafari } from '../utils/common';
import { SafariGradientFixCanvasFactory } from '../utils/safariCanvasFactory';
interface UsePdfLoadResult {
    pdfDoc: any;
    loading: boolean;
    error: any;
    reload: () => void;
    totalPages: number;
    /** 是否启用双指捏合缩放，由 getPdfUrlApi 返回的 disabledPinchZoom 控制，默认启用 */
    pinchZoomEnabled: boolean;
    /** 是否启用 CJK 字符渲染修复 */
    enableCjkFix: boolean;
}

/** 标记 pdfjsLib 初始化失败，不可通过重试恢复的错误 */
class PdfjsLibNotFoundError extends Error {
    /** 标记为不可重试，避免无意义的重复加载 */
    readonly retryable = false;
    constructor(waitedMs: number) {
        super(
            `[usePdfLoad] pdfjsLib not found after script load (polled ${waitedMs}ms). ` +
            `UA: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'}`,
        );
        this.name = 'PdfjsLibNotFoundError';
    }
}

/** PDF.js 及运行环境产生的已知告警，对渲染无影响，过滤以保持控制台干净 */
const PDFJS_WARN_SUPPRESS_PATTERNS = [
    /Not enough parameters for (hstem|vstem)/i,
    /Too many (hstem|vstem) commands/i,
    /document\.domain mutation is ignored/i,
];

/**
 * 安装 PDF.js 字体解析告警过滤，返回恢复函数。
 * 用于在 PDF 加载与渲染期间保持控制台干净，卸载时调用恢复。
 */
function installPdfWarnSuppress(): () => void {
    const noop = (..._args: unknown[]) => { return; };
    const origWarn = typeof console !== 'undefined' ? console.warn : noop;
    let restored = false;
    const restore = () => {
        if (restored) return;
        restored = true;
        console.warn = origWarn;
    };
    console.warn = (...args: unknown[]) => {
        const msg = args.length > 0 ? String(args[0]) : '';
        if (PDFJS_WARN_SUPPRESS_PATTERNS.some((p) => p.test(msg))) return;
        origWarn.apply(console, args);
    };
    return restore;
}

/**
 * 在 script onload 之后轮询等待 pdfjsLib 挂载到 window。
 * 旧版 iOS WebView（如 iOS 13）中 onload 触发时脚本可能尚未完成执行，
 * 轮询可以兜住这种延迟初始化场景。
 */
function waitForPdfjsLib(timeout = 3000, interval = 100): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        if (typeof $window.pdfjsLib !== 'undefined') {
            resolve();
            return;
        }
        const start = Date.now();
        const timer = setInterval(() => {
            if (typeof $window.pdfjsLib !== 'undefined') {
                clearInterval(timer);
                resolve();
            } else if (Date.now() - start >= timeout) {
                clearInterval(timer);
                reject(new PdfjsLibNotFoundError(Date.now() - start));
            }
        }, interval);
    });
}

export function usePdfLoad(props: IPdfReaderProps): UsePdfLoadResult {
    const { resourceUrl, elementId, cMapUrl, pdfJsWorkerUrl, onError, transStatus } = props;
    const [pdfDoc, setPdfDoc] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [pinchZoomEnabled, setPinchZoomEnabled] = useState<boolean>(true);
    const [enableCjkFix, setEnableCjkFix] = useState<boolean>(false);
    const maxRetries = useRef(3);
    const retryCount = useRef(0);
    /** 缓存 API 返回的配置，重试时复用，避免重复请求 */
    const cachedConfig = useRef<IOutput | null>(null);
    /** PDF.js 告警过滤的恢复函数，卸载时调用 */
    const pdfWarnSuppressRestoreRef = useRef<(() => void) | null>(null);

    const loadPdf = useCallback(
        async (isRetry = false) => {
            if (!resourceUrl) {
                setLoading(false);
                return;
            }
            if (!isRetry) {
                setLoading(true);
                setError(null);
                retryCount.current = 0;
                cachedConfig.current = null;
            }

            try {
                // 1. 获取 PDF 配置和 URL（重试时复用缓存，避免冗余请求）
                let res: IOutput;
                if (isRetry && cachedConfig.current) {
                    res = cachedConfig.current;
                } else {
                    const params: IInput = { isIos };
                    if (elementId) {
                        params.elementId = elementId;
                    }
                    res = await getPdfUrlApi(params);
                    cachedConfig.current = res;
                }
                setPinchZoomEnabled(!(res as any)?.disabledPinchZoom); // eslint-disable-line @typescript-eslint/no-explicit-any
                setEnableCjkFix(
                    isIos && res.iosCjk === true && iosVersion < Number.parseFloat(res.iosCjkVersion ?? '0')
                );
                if (!res?.pdfJsUrl) {
                    throw new Error('[usePdfLoad] PDF Config Error: pdfJsUrl is empty');
                }

                // 2. 加载 PDF.js 脚本（如果 pdfjsLib 已存在则跳过，防止重复插入 <script>）
                if (typeof $window.pdfjsLib === 'undefined') {
                    // 先移除之前可能残留的同 src 脚本标签，防止 DOM 中堆积多余 <script>
                    document.querySelectorAll(`script[src="${res.pdfJsUrl}"]`).forEach((s) => {
                        s.remove();
                    });
                    await loadScriptAsync([res.pdfJsUrl]);
                }

                // 3. 等待配置加载完成
                await new Promise<void>((resolve) => {
                    let resolved = false;
                    const resolveOnce = () => {
                        if (!resolved) {
                            resolved = true;
                            resolve();
                        }
                    };

                    configRenderHelper.onConfigLoaded(() => {
                        resolveOnce();
                    });

                    configRenderHelper.loadConfig();

                    if (configRenderHelper.isLoaded) {
                        resolveOnce();
                    }
                });

                // 4. 轮询等待 pdfjsLib 挂载（兼容旧版 iOS WebView 延迟初始化）
                await waitForPdfjsLib();

                // 5. 初始化 PDF 文档
                // 优先使用 props 传入的 workerUrl，否则使用 API 返回的
                const finalWorkerUrl = pdfJsWorkerUrl || res.pdfJsWorkerUrl;
                if (finalWorkerUrl) {
                    $window.pdfjsLib.GlobalWorkerOptions.workerSrc = finalWorkerUrl;
                }

                const finalResourceUrl = `${resourceUrl}?v=mobile`;
                /**
                 * 关键兼容：迁移前的 DocReader 会把 getPdfUrlApi 返回的 cMapUrl 透传给 getDocument。
                 * 迁移后如果仅依赖 props.cMapUrl（多数调用方未传），会导致 CJK/CID 字体映射缺失，
                 * 表现为 Canvas/SVG 两种渲染下都出现"部分中文/日文段落不显示/缺字"。
                 */
                const finalCMapUrl = cMapUrl || (res as any)?.cMapUrl;
                const wasmMapUrl = (res as any)?.wasmMapUrl;
                const iccUrlMapUrl = (res as any)?.iccUrlMapUrl;
                const docParams: Record<string, any> = {
                    url: finalResourceUrl,
                    verbosity: 0,// 抑制大量 console.warn 避免页面卡顿（飞书 WebView 等场景尤为重要）
                };
                if (finalCMapUrl) {
                    docParams.cMapUrl = finalCMapUrl;
                    docParams.cMapPacked = true;// 启用 CMap 压缩，减少体积（本次新增）
                }
                if (wasmMapUrl) {
                    // JPEG 2000 图片解码（有 JPX 格式图片时必须）
                    docParams.wasmUrl = wasmMapUrl;
                }
                if (iccUrlMapUrl) {
                    // ICC 色彩空间精确还原（CMYK PDF）
                    docParams.iccUrl = iccUrlMapUrl;
                }
                // 如果 API 返回了 standardFontDataUrl（某些 pdf.js 构建需要），一并透传以增强字体兼容性
                if ((res as any)?.standardFontDataUrl) {
                    docParams.standardFontDataUrl = (res as any).standardFontDataUrl;
                }

                if (checkIsAuthCDNUrl(finalResourceUrl)) {
                    docParams.withCredentials = true;
                }
                if (isIOSSafari && !isHarmonyOSDevice) {
                    // Safari/iOS：pdf.js Worker 处理超大嵌入图像（如 6000×8000 px）时，
                    // 会在 Worker 内调用 ctx.putImageData(new ImageData(w, h), 0, 0)。
                    // 当图像面积超过 Safari IOSurface 16M 像素上限时，putImageData 静默失败
                    // （不抛异常），导致 canvas 全空白——即"组件未渲染"。
                    // 设置 canvasMaxAreaInBytes 后，pdf.js 的 ImageResizer 会在 putImageData
                    // 之前先将图像缩小至安全尺寸，从而正常渲染。
                    // 鸿蒙排除：isIOSSafari 包含 isHarmonyOS()，鸿蒙应走独立修复路径。
                    docParams.canvasMaxAreaInBytes = 16_000_000 * 4; // 16M 像素 × 4 字节/像素
                    // Luminosity SMask destination-in 修复：
                    // pdfjs 用 destination-in 实现 Luminosity SMask，Safari 在嵌套 save/restore
                    // 中对该操作的 alpha 处理有偏差，导致渐变层全不透明遮盖底层图片。
                    // 注入自定义 CanvasFactory，在每个 context 上拦截 destination-in drawImage，
                    // 改用像素操作（getImageData/putImageData）手动应用亮度掩码。
                    docParams.CanvasFactory = SafariGradientFixCanvasFactory;
                }

                const loadingTask = $window.pdfjsLib.getDocument(docParams);
                const pdf = await loadingTask.promise;
                setPdfDoc(pdf);
                setTotalPages(pdf.numPages);
                setLoading(false);
            } catch (err: any) {
                // 不可重试的错误（如脚本在当前浏览器无法执行），直接上报，不再浪费重试次数
                const isRetryable = err?.retryable !== false;

                if (isRetryable && retryCount.current < maxRetries.current) {
                    retryCount.current++;
                    const delay = Math.min(1000 * 2 ** retryCount.current, 5000);
                    setTimeout(() => {
                        loadPdf(true);
                    }, delay);
                } else {
                    setError(err);
                    setLoading(false);
                    onError?.({
                        error: err,
                        componentName: 'usePdfLoad',
                        props: { resourceUrl, elementId },
                    });
                }
            }
        },
        [resourceUrl, elementId, cMapUrl, pdfJsWorkerUrl, onError],
    );

    useEffect(() => {
        // Only load if resourceUrl is provided and not waiting for transcoding
        if (resourceUrl && transStatus !== ResourceStatus.WAIT_TRANSCODING) {
            pdfWarnSuppressRestoreRef.current = installPdfWarnSuppress();
            loadPdf();
        } else if (transStatus === ResourceStatus.WAIT_TRANSCODING) {
            // Keep loading state when waiting for transcoding
            setLoading(true);
        } else {
            setLoading(false);
        }
        return () => {
            const restore = pdfWarnSuppressRestoreRef.current;
            if (restore) restore();
            pdfWarnSuppressRestoreRef.current = null;
            // 不在卸载时调用 pdfDoc.destroy()：PDF.js 使用共享 Worker，destroy 会破坏 Worker 状态，
            // 导致再次打开详情时文档元数据可加载（工具栏更新）但 getPage/渲染走 Worker 失败，内容不显示且无报错。
        };
    }, [loadPdf, resourceUrl, transStatus]);

    const reload = useCallback(() => {
        loadPdf();
    }, [loadPdf]);

    return { pdfDoc, loading, error, reload, totalPages, pinchZoomEnabled, enableCjkFix };
}
